<template>
  <div>
    <Popover v-slot="{ open, close }" class="sm:relative">
      <PopoverButton
        class="relative flex items-center justify-center rounded-full p-1 text-gray-400 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-gray-900"
        @click="checkPermission(open)"
      >
        <BellIcon class="size-6" aria-hidden="true" />
        <span class="sr-only">Notifications</span>
        <CountBadge
          v-if="isFirebaseSupported() && newNotificationsCount"
          class="z-notification absolute !-right-1 !-top-1"
          :count="newNotificationsCount"
          :max="99"
        />
      </PopoverButton>

      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-y-1 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-1 opacity-0"
      >
        <PopoverPanel
          class="z-notification absolute inset-x-0 z-10 mt-4 flex w-screen max-w-max sm:left-auto sm:mt-2"
        >
          <div
            class="w-screen flex-auto overflow-hidden bg-white text-sm leading-6 shadow-lg ring-1 ring-black/5 sm:max-w-sm sm:rounded-md"
          >
            <div
              class="relative flex items-center justify-between border-b border-b-gray-200 bg-gray-50 p-4 pr-16 sm:pr-4"
            >
              <span class="h-8 text-base font-medium leading-8 text-gray-900">
                {{ $t('user-notifications.title') }}
              </span>

              <div class="flex items-center space-x-2">
                <span
                  v-if="isReadAllVisible"
                  class="clickable-text inline-flex h-8 items-center"
                  @click="updateAllAsRead"
                >
                  {{ $t('user-notifications.mark-all-as-read') }}
                </span>

                <Button
                  href="/notifications"
                  type="secondary"
                  icon-only
                  @click="close"
                >
                  <ArrowTopRightOnSquareIcon
                    class="size-4 text-gray-500"
                    aria-hidden="true"
                  />
                </Button>
              </div>

              <button
                type="button"
                class="absolute right-0 mr-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:hidden"
                @click="close"
              >
                <span class="sr-only">Close notifications menu</span>
                <XMarkIcon class="block size-6" aria-hidden="true" />
              </button>
            </div>

            <div
              class="max-h-[calc(100vh-130px)] overflow-y-auto p-4 sm:max-h-96"
            >
              <div
                v-if="isFirebaseSupported() && userNotificationsResponse.length"
                class="space-y-4"
              >
                <UserNotificationItem
                  v-for="(notification, index) in userNotificationsResponse"
                  :key="index"
                  :user-notification="notification"
                  @updated="updatedReadStatus"
                  @click-action="onNotificationClick(notification, close)"
                />

                <Button
                  v-if="userNotificationsResponse.length < totalNotifications"
                  type="soft"
                  size="lg"
                  class="w-full"
                  :loading="isLoadingUserNotification"
                  @click="showMore"
                >
                  {{ $t('general.show-more') }}
                </Button>
                <Button
                  v-else-if="
                    userNotificationsResponse.length >
                      defaultUserNotificationSize
                  "
                  :loading="isLoadingUserNotification"
                  type="soft"
                  size="lg"
                  class="w-full"
                  @click="showLess"
                >
                  {{ $t('general.show-less') }}
                </Button>
              </div>
              <EmptyState
                v-else-if="!isFirebaseSupported()"
                :title="$t('user-notifications.unsupported-title')"
                :subtitle="$t('user-notifications.unsupported-message')"
                :icon="NoSymbolIcon"
                class="!my-8"
              />
              <EmptyState
                v-else
                :title="$t('user-notifications.empty-title')"
                :subtitle="$t('user-notifications.empty-message')"
                :icon="ArchiveBoxIcon"
                class="!my-8"
              />
            </div>
          </div>

          <LoadingOverlay v-if="isLoadingUserNotification || isUpdateLoading" />
        </PopoverPanel>
      </transition>
    </Popover>

    <ClientTaskSlideOver
      v-model="isClientTaskSlideOverVisible"
      :client-task-id="clientTaskId"
      show-open-client-button
    />
  </div>
</template>

<script lang="ts" setup>
// DOCS: https://rollouter.atlassian.net/wiki/spaces/RCP/pages/71991297/User+Notifications

import {
  ArchiveBoxIcon,
  BellIcon,
  XMarkIcon,
  NoSymbolIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/vue/24/outline';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { useUserStore } from '@/stores/user';
import { isFirebaseSupported } from '~/util/firebase';
import {
  defaultUserNotificationSize,
  useUserNotificationsStore,
} from '~/stores/user-notification';
import type { UserNotificationResponseItem } from '~/composables/api-services/api-notfication';
import { useNotifications } from '~/stores/notifications';

// User Notifications

const { $client } = useNuxtApp();
const userStore = useUserStore();
const i18n = useI18n();

const notificationSize = ref(defaultUserNotificationSize);

const userNotificationsStore = useUserNotificationsStore();

const userNotificationsResponse = computed(
  () => userNotificationsStore.getUserNotifications
);
const isLoadingUserNotification = computed(
  () => userNotificationsStore.isLoading
);
const totalNotifications = computed(() => userNotificationsStore.totalElements);

const { notificationClickAction, isClientTaskSlideOverVisible, clientTaskId } =
  useUserNotifications();

const newNotificationsCount = computed(
  () => userNotificationsStore.unread ?? 0
);

const isReadAllVisible = computed<boolean>(() =>
  userNotificationsResponse.value.some((notification) => {
    const isRead =
      notification.users.find((user) => {
        return user.user?._id === userStore.user?._id.toString();
      })?.isViewed ?? false;
    return !isRead;
  })
);

const isUpdateLoading = ref(false);

const updateAllAsRead = async () => {
  if (isUpdateLoading.value) {
    return;
  }
  try {
    await $client.notification.updateAllAsViewedNotifications.mutate();
  } catch (error) {
    useErrorHandler(error, {
      type: 'error',
      message: i18n.t('error-messages.something-went-wrong'),
    });
  } finally {
    isUpdateLoading.value = false;
    await userNotificationsStore.fetch({ size: notificationSize.value });
  }
};

const showMore = () => {
  notificationSize.value = notificationSize.value + defaultUserNotificationSize;
  userNotificationsStore.fetch({ size: notificationSize.value });
};

const showLess = () => {
  notificationSize.value = defaultUserNotificationSize;
  userNotificationsStore.fetch({ size: notificationSize.value });
};

const onNotificationClick = (
  notification: UserNotificationResponseItem,
  closeFn: () => void
) => {
  closeFn();
  notificationClickAction(notification, notificationSize.value);
};

const updatedReadStatus = () => {
  userNotificationsStore.fetch({ size: notificationSize.value });
};

// Browser notification permission

const notifications = useNotifications();

const checkPermission = (isMenuClosing: boolean) => {
  if ('Notification' in window) {
    if (Notification.permission !== 'granted' && !isMenuClosing) {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          window.location.reload();
        } else {
          notifications.createNotification({
            type: 'error',
            title: i18n.t('user-notifications.permission-rejected'),
            message: i18n.t('user-notifications.permission-rejected-message'),
          });
        }
      });
    }
  }
};
</script>
